import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { DEFAULT_CATEGORIES } from '../utils/constants';

const useCategoryStore = create(
  subscribeWithSelector((set, get) => ({
    // State
    categories: [],
    loading: false,
    error: null,
    unsubscribeCategories: null,

    // Actions
    initializeDefaultCategories: async (familyId, userId) => {
      set({ loading: true, error: null });
      try {
        const categoriesRef = collection(db, `families/${familyId}/categories`);
        
        // Check if categories already exist
        const existingCategories = await getDocs(categoriesRef);
        if (!existingCategories.empty) {
          set({ loading: false });
          return { success: true, message: 'Categories already exist' };
        }

        // Add default categories
        const promises = DEFAULT_CATEGORIES.map(category => 
          addDoc(categoriesRef, {
            ...category,
            isDefault: true,
            createdAt: new Date(),
            createdBy: userId
          })
        );

        await Promise.all(promises);
        
        set({ loading: false, error: null });
        return { success: true };
      } catch (error) {
        set({ loading: false, error: error.message });
        return { success: false, error: error.message };
      }
    },

    // Force update categories to latest structure
    updateToLatestCategories: async (familyId, userId) => {
      set({ loading: true, error: null });
      try {
        const categoriesRef = collection(db, `families/${familyId}/categories`);
        
        // Delete all existing categories
        const existingCategories = await getDocs(categoriesRef);
        const deletePromises = existingCategories.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deletePromises);

        // Add new default categories
        const promises = DEFAULT_CATEGORIES.map(category => 
          addDoc(categoriesRef, {
            ...category,
            isDefault: true,
            createdAt: new Date(),
            createdBy: userId
          })
        );

        await Promise.all(promises);
        
        set({ loading: false, error: null });
        return { success: true };
      } catch (error) {
        set({ loading: false, error: error.message });
        return { success: false, error: error.message };
      }
    },

    subscribeToCategories: (familyId) => {
      if (!familyId) return;

      // Unsubscribe from previous subscription
      const { unsubscribeCategories } = get();
      if (unsubscribeCategories) {
        unsubscribeCategories();
      }

      const categoriesRef = collection(db, `families/${familyId}/categories`);
      const q = query(categoriesRef, orderBy('order', 'asc'));

      const unsubscribe = onSnapshot(q, 
        (snapshot) => {
          const categories = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          set({ 
            categories, 
            loading: false, 
            error: null,
            unsubscribeCategories: unsubscribe
          });
        },
        (error) => {
          console.error('Error subscribing to categories:', error);
          set({ error: error.message, loading: false });
        }
      );

      set({ unsubscribeCategories: unsubscribe });
    },

    getCategories: async (familyId) => {
      if (!familyId) return { success: false, error: 'No family ID provided' };

      set({ loading: true, error: null });
      try {
        const categoriesRef = collection(db, `families/${familyId}/categories`);
        const q = query(categoriesRef, orderBy('order', 'asc'));
        const snapshot = await getDocs(q);
        
        const categories = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        set({ categories, loading: false, error: null });
        return { success: true, categories };
      } catch (error) {
        set({ loading: false, error: error.message });
        return { success: false, error: error.message };
      }
    },

    createCategory: async (familyId, userId, categoryData) => {
      if (!familyId || !userId) return { success: false, error: 'Missing required parameters' };

      set({ loading: true, error: null });
      try {
        const categoriesRef = collection(db, `families/${familyId}/categories`);
        
        // Get the highest order number
        const { categories } = get();
        const maxOrder = categories.length > 0 ? Math.max(...categories.map(c => c.order)) : 0;

        const newCategory = {
          ...categoryData,
          order: maxOrder + 1,
          isDefault: false,
          subcategories: categoryData.subcategories || [],
          createdAt: new Date(),
          createdBy: userId
        };

        const docRef = await addDoc(categoriesRef, newCategory);
        
        set({ loading: false, error: null });
        return { success: true, id: docRef.id };
      } catch (error) {
        set({ loading: false, error: error.message });
        return { success: false, error: error.message };
      }
    },

    updateCategory: async (familyId, categoryId, updates) => {
      if (!familyId || !categoryId) return { success: false, error: 'Missing required parameters' };

      set({ loading: true, error: null });
      try {
        const categoryRef = doc(db, `families/${familyId}/categories`, categoryId);
        await updateDoc(categoryRef, {
          ...updates,
          updatedAt: new Date()
        });

        set({ loading: false, error: null });
        return { success: true };
      } catch (error) {
        set({ loading: false, error: error.message });
        return { success: false, error: error.message };
      }
    },

    deleteCategory: async (familyId, categoryId) => {
      if (!familyId || !categoryId) return { success: false, error: 'Missing required parameters' };

      set({ loading: true, error: null });
      try {
        const categoryRef = doc(db, `families/${familyId}/categories`, categoryId);
        await deleteDoc(categoryRef);

        set({ loading: false, error: null });
        return { success: true };
      } catch (error) {
        set({ loading: false, error: error.message });
        return { success: false, error: error.message };
      }
    },

    reorderCategories: async (familyId, reorderedCategories) => {
      if (!familyId) return { success: false, error: 'No family ID provided' };

      set({ loading: true, error: null });
      try {
        const promises = reorderedCategories.map((category, index) => {
          const categoryRef = doc(db, `families/${familyId}/categories`, category.id);
          return updateDoc(categoryRef, { order: index + 1 });
        });

        await Promise.all(promises);
        
        set({ loading: false, error: null });
        return { success: true };
      } catch (error) {
        set({ loading: false, error: error.message });
        return { success: false, error: error.message };
      }
    },

    getCategoryById: (categoryId) => {
      const { categories } = get();
      return categories.find(category => category.id === categoryId);
    },

    getCategoriesByIds: (categoryIds) => {
      const { categories } = get();
      return categories.filter(category => categoryIds.includes(category.id));
    },

    clearError: () => set({ error: null }),

    cleanup: () => {
      const { unsubscribeCategories } = get();
      if (unsubscribeCategories) {
        unsubscribeCategories();
        set({ unsubscribeCategories: null });
      }
    }
  }))
);

export default useCategoryStore;

